import { useEffect, useState } from 'react';

import {
  isIOS,
  isMobile,
  isTablet,
  getUA,
  mobileVendor,
  mobileModel,
  browserName,
  deviceDetect,
} from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import env from '../../env';
import { trackActionAnnonymous, trackPageAnnonymous } from './partnerAnalytics';
import { KSOURCE } from '../Hooks/usePageViewTracking';
const USER_SESSION_ID = 'SessionTrackingID';
const TIME_SPENT_SESSION_TRACKING = 'TimeSpentSessionTracking';
const PAGE_VIEW_SESSION_TRACKING = 'PageViewSessionTracking';

export type PartnerNames =
  | 'TIM'
  | 'Kindred'
  | 'Eon'
  | 'Sliide'
  | 'NTV'
  | 'NationalRail'
  | 'BlueLightCard';

const PartnerIDS: Record<string, string> = {
  TIM: env.tim_partner_id,
  Kindred: env.kindred_partner_id,
  Eon: env.eon_partner_id,
  NTV: env.ntv_partner_id,
  NationalRail: env.national_rail_partner_id,
  BlueLightCard: env.blue_light_card_partner_id,
};

export const hasTrackedOnSession = (
  pageName: string,
  pageViewSession: boolean
) => {
  let sessionTracked = true;
  const sessionName = pageViewSession
    ? PAGE_VIEW_SESSION_TRACKING
    : TIME_SPENT_SESSION_TRACKING;

  const trackedPages = sessionStorage.getItem(sessionName) || '';

  const trackedPagesArray = trackedPages.split(',').filter(Boolean);
  if (!trackedPagesArray.includes(pageName)) {
    sessionTracked = false;
    trackedPagesArray.push(pageName);
    sessionStorage.setItem(sessionName, trackedPagesArray.join(','));
  }

  return sessionTracked;
};

export const getLocalUserID = () => {
  let localUserID = sessionStorage.getItem(USER_SESSION_ID);

  if (!localUserID) {
    const params = new URLSearchParams(window.location.search);
    // checking if a session Id was passed so we can use it as userID
    const sessionUserID = params.get('sessionID');

    localUserID = sessionUserID || (uuidv4() as string);
    sessionStorage.setItem(USER_SESSION_ID, localUserID);
  }
  return localUserID;
};

const getPlatform = () => {
  if (isMobile) {
    return isIOS ? 'iOS' : 'Android';
  } else if (isTablet) {
    return 'tablet';
  } else {
    return `Desktop (${getBrowserName()})`;
  }
};

const getOperatingSystem = () => {
  let os = 'unknown';
  const agent = deviceDetect(getUA);

  if ((agent.osName || agent.os) && agent.osVersion) {
    os = (agent.osName || agent.os) + ' ' + agent.osVersion;
  }

  return os;
};

export const getDevice = () => {
  let device = 'unknown';
  const userAgent = getUA.toLowerCase();
  if (userAgent.includes('windows')) {
    device = 'Windows';
  } else if (userAgent.includes('mac os') || userAgent.includes('macos')) {
    if (isMobile) {
      device = 'iPhone';
    } else if (isTablet) {
      device = 'iPad';
    } else {
      device = 'MacOS';
    }
  } else if (mobileVendor === 'none') {
    device = getOperatingSystem();
  } else {
    device = `${mobileVendor} ${mobileModel}`.trim() || 'Unknown';
  }
  return device;
};

const getBrowserName = () => {
  if (browserName === 'Chrome') {
    if (isMobile) {
      return `Mobile ${browserName}`;
    } else if (isTablet) {
      return `Tablet ${browserName}`;
    }
  }
  return browserName;
};

const getPartnersStandardProps = (partner = 'Kindred') => {
  return {
    Partner: partner,
    Platform: getPlatform(),
    OperatingSystem: getOperatingSystem(),
    Browser: getBrowserName(),
    Device: getDevice(),
  };
};
export const trackPageViewForPartner = (
  pageName: string,
  partner: PartnerNames,
  ksource: string | null | undefined,
  properties: Record<string, unknown> | undefined = {}
) => {
  trackPageAnnonymous(
    pageName,
    {
      Ksource: ksource,
      ...getPartnersStandardProps(partner),
      ...properties,
    },
    PartnerIDS[partner]
  );
};

export const trackClickedActionEvent = (
  query: URLSearchParams,
  partner: PartnerNames,
  label: string,
  pageName: string
) => {
  trackPartnerActionEvent(
    ANALYTICS_ACTION_NAMES.clicked_action,
    partner,
    query,
    {
      Label: label,
      PageName: pageName,
    }
  );
};

export const trackActivationActionEvent = (
  query: URLSearchParams,
  partner: PartnerNames,
  label: string,
  pageName: string,
  properties: Record<string, unknown> | undefined = {}
) => {
  trackPartnerActionEvent(
    ANALYTICS_ACTION_NAMES.activation_action,
    partner,
    query,
    {
      Label: label,
      PageName: pageName,
      ...properties,
    }
  );
};

export const trackPartnerActionEvent = (
  eventName: string,
  partner: PartnerNames,
  query: URLSearchParams,
  properties: Record<string, unknown> | undefined = {}
) => {
  const ksource = query.get(KSOURCE);
  trackActionAnnonymous(
    eventName,
    {
      Ksource: ksource,
      ...getPartnersStandardProps(partner),
      ...properties,
    },
    PartnerIDS[partner]
  );
};

export function useIsIPad() {
  const [isIPad, setIsIPad] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor;

    // Classic iPads
    if (/iPad/.test(ua)) {
      setIsIPad(true);
      return;
    }

    // iPadOS 13+ (reports as "Macintosh" but supports touch)
    if (/Macintosh/.test(ua) && 'ontouchend' in document) {
      setIsIPad(true);
      return;
    }

    setIsIPad(false);
  }, []);

  return isIPad;
}

export const ANALYTICS_PAGE_NAMES = {
  activation_complete_page: 'activation_complete_page',
  accessibility_instructions_page: 'accessibility_instructions_page',
  additional_permissions_page: 'additional_permissions_page',
  partially_installed_page: 'partially_installed_page',
  activation_instructions_page: 'activation_instructions_page',
  landing_page: 'landing_page',
  extension_explainer: 'extension_explainer',
  mobile_disabled_page: 'mobile_disabled_page',
  kill_switch_active_page: 'kill_switch_active_page',
  safari_desktop_instructions_page: 'safari_desktop_instructions_page',
  safari_desktop_success_page: 'safari_desktop_success_page',
  chrome_desktop_success_page: 'chrome_desktop_success_page',
  desktop_activation_landing_page: 'desktop_activation_landing_page',
};

export const ANALYTICS_ACTION_NAMES = {
  clicked_action: 'clicked_action',
  activation_action: 'activation_action',
};

export const ANALYTICS_ACTION_LABELS = {
  activation_permissions: 'activation_permissions',
  checkout_deals: 'checkout_deals',
  desktop_extension_install: 'desktop_extension_install',
  back_to_partner_site: 'back_to_partner_site',
};
